import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const RecruitmentPage = () => {
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('jobs');
  const [showJobModal, setShowJobModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobForm, setJobForm] = useState({
    title: '',
    department: '',
    location: '',
    job_type: 'full_time',
    description: '',
    requirements: '',
    salary_range_min: '',
    salary_range_max: '',
    positions_available: 1
  });
  const [candidateForm, setCandidateForm] = useState({
    job_id: '',
    name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    cover_letter: '',
    source: 'internal'
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchJobs();
    fetchCandidates();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/hr/recruitment/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Erro ao carregar vagas');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await api.get('/hr/recruitment/candidates');
      setCandidates(response.data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/recruitment/jobs', jobForm);
      toast.success('Vaga criada com sucesso');
      setShowJobModal(false);
      setJobForm({
        title: '',
        department: '',
        location: '',
        job_type: 'full_time',
        description: '',
        requirements: '',
        salary_range_min: '',
        salary_range_max: '',
        positions_available: 1
      });
      fetchJobs();
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar vaga');
    }
  };

  const handleCreateCandidate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/recruitment/candidates', candidateForm);
      toast.success('Candidato adicionado com sucesso');
      setShowCandidateModal(false);
      setCandidateForm({
        job_id: '',
        name: '',
        email: '',
        phone: '',
        linkedin_url: '',
        cover_letter: '',
        source: 'internal'
      });
      fetchCandidates();
    } catch (error) {
      console.error('Error creating candidate:', error);
      toast.error(error.response?.data?.detail || 'Erro ao adicionar candidato');
    }
  };

  const handleUpdateStage = async (candidateId, newStage) => {
    try {
      await api.put(`/hr/candidates/${candidateId}/stage?new_stage=${newStage}`);
      toast.success('Fase atualizada');
      fetchCandidates();
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Erro ao atualizar fase');
    }
  };

  const getJobTypeBadge = (type) => {
    const labels = {
      full_time: 'Tempo Inteiro',
      part_time: 'Part-Time',
      contract: 'Contrato',
      internship: 'Estágio'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: 'bg-emerald-100 text-emerald-700',
      draft: 'bg-gray-100 text-gray-700',
      on_hold: 'bg-amber-100 text-amber-700',
      closed: 'bg-red-100 text-red-700',
      filled: 'bg-blue-100 text-blue-700'
    };
    const labels = {
      open: 'Aberta',
      draft: 'Rascunho',
      on_hold: 'Em Espera',
      closed: 'Fechada',
      filled: 'Preenchida'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getStageBadge = (stage) => {
    const colors = {
      applied: 'bg-gray-100 text-gray-700',
      screening: 'bg-blue-100 text-blue-700',
      interview: 'bg-indigo-100 text-indigo-700',
      technical_test: 'bg-purple-100 text-purple-700',
      offer: 'bg-amber-100 text-amber-700',
      hired: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const labels = {
      applied: 'Candidatura',
      screening: 'Triagem',
      interview: 'Entrevista',
      technical_test: 'Teste Técnico',
      offer: 'Oferta',
      hired: 'Contratado',
      rejected: 'Rejeitado'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[stage] || 'bg-gray-100 text-gray-700'}`}>
        {labels[stage] || stage}
      </span>
    );
  };

  const stages = ['applied', 'screening', 'interview', 'technical_test', 'offer', 'hired'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="recruitment-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Recrutamento</h1>
          <p className="text-muted-foreground">Gestão de vagas e candidatos</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCandidateModal(true)}
            className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Novo Candidato
          </button>
          <button
            onClick={() => setShowJobModal(true)}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nova Vaga
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('jobs')}
          className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'jobs' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Vagas ({jobs.length})
        </button>
        <button
          onClick={() => setActiveTab('candidates')}
          className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'candidates' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Candidatos ({candidates.length})
        </button>
        <button
          onClick={() => setActiveTab('pipeline')}
          className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'pipeline' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Pipeline
        </button>
      </div>

      {/* Jobs Tab */}
      {activeTab === 'jobs' && (
        <div className="grid gap-4">
          {jobs.length === 0 ? (
            <div className="rounded-xl border bg-card p-8 text-center">
              <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-muted-foreground">Sem vagas abertas</p>
            </div>
          ) : (
            jobs.map((job) => (
              <div key={job.job_id} className="rounded-xl border bg-card p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{job.title}</h3>
                      {getStatusBadge(job.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {job.department && <span>{job.department}</span>}
                      {job.location && <span>{job.location}</span>}
                      <span>{getJobTypeBadge(job.job_type)}</span>
                    </div>
                    {(job.salary_range_min || job.salary_range_max) && (
                      <p className="text-sm text-emerald-600 mt-1">
                        {job.salary_range_min && job.salary_range_max 
                          ? `${job.salary_range_min.toLocaleString('pt-PT')}€ - ${job.salary_range_max.toLocaleString('pt-PT')}€`
                          : job.salary_range_min 
                            ? `A partir de ${job.salary_range_min.toLocaleString('pt-PT')}€`
                            : `Até ${job.salary_range_max.toLocaleString('pt-PT')}€`
                        }
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{candidates.filter(c => c.job_id === job.job_id).length}</p>
                      <p className="text-xs text-muted-foreground">Candidatos</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{job.positions_available}</p>
                      <p className="text-xs text-muted-foreground">Posições</p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Candidates Tab */}
      {activeTab === 'candidates' && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Candidato</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Vaga</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Fonte</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Fase</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Data</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {candidates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      Sem candidatos
                    </td>
                  </tr>
                ) : (
                  candidates.map((candidate) => {
                    const job = jobs.find(j => j.job_id === candidate.job_id);
                    return (
                      <tr key={candidate.candidate_id} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{candidate.name}</p>
                            <p className="text-sm text-muted-foreground">{candidate.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">{job?.title || '-'}</td>
                        <td className="px-4 py-3 text-sm">{candidate.source}</td>
                        <td className="px-4 py-3">{getStageBadge(candidate.stage)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(candidate.created_at).toLocaleDateString('pt-PT')}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={candidate.stage}
                            onChange={(e) => handleUpdateStage(candidate.candidate_id, e.target.value)}
                            className="px-2 py-1 rounded border border-input bg-background text-sm"
                          >
                            {stages.map(stage => (
                              <option key={stage} value={stage}>{stage}</option>
                            ))}
                            <option value="rejected">rejected</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pipeline Tab - Kanban View */}
      {activeTab === 'pipeline' && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {stages.map((stage) => {
              const stageCandidates = candidates.filter(c => c.stage === stage);
              return (
                <div key={stage} className="w-72 flex-shrink-0">
                  <div className="rounded-xl border bg-card">
                    <div className="p-4 border-b border-border">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{getStageBadge(stage)}</h3>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-muted">{stageCandidates.length}</span>
                      </div>
                    </div>
                    <div className="p-2 space-y-2 min-h-[200px]">
                      {stageCandidates.map((candidate) => {
                        const job = jobs.find(j => j.job_id === candidate.job_id);
                        return (
                          <div key={candidate.candidate_id} className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                            <p className="font-medium text-sm">{candidate.name}</p>
                            <p className="text-xs text-muted-foreground">{job?.title || 'Candidatura espontânea'}</p>
                            <p className="text-xs text-muted-foreground mt-1">{candidate.email}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Job Modal */}
      {showJobModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Nova Vaga</h2>
            </div>
            <form onSubmit={handleCreateJob} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Título da Vaga</label>
                  <input
                    type="text"
                    value={jobForm.title}
                    onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Departamento</label>
                  <input
                    type="text"
                    value={jobForm.department}
                    onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Localização</label>
                  <input
                    type="text"
                    value={jobForm.location}
                    onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo</label>
                  <select
                    value={jobForm.job_type}
                    onChange={(e) => setJobForm({ ...jobForm, job_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="full_time">Tempo Inteiro</option>
                    <option value="part_time">Part-Time</option>
                    <option value="contract">Contrato</option>
                    <option value="internship">Estágio</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Posições</label>
                  <input
                    type="number"
                    min="1"
                    value={jobForm.positions_available}
                    onChange={(e) => setJobForm({ ...jobForm, positions_available: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Salário Mínimo (€)</label>
                  <input
                    type="number"
                    value={jobForm.salary_range_min}
                    onChange={(e) => setJobForm({ ...jobForm, salary_range_min: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Salário Máximo (€)</label>
                  <input
                    type="number"
                    value={jobForm.salary_range_max}
                    onChange={(e) => setJobForm({ ...jobForm, salary_range_max: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    placeholder="Opcional"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Descrição</label>
                  <textarea
                    value={jobForm.description}
                    onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    rows={4}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Requisitos</label>
                  <textarea
                    value={jobForm.requirements}
                    onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    rows={4}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowJobModal(false)}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Criar Vaga
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Candidate Modal */}
      {showCandidateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Novo Candidato</h2>
            </div>
            <form onSubmit={handleCreateCandidate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Vaga</label>
                <select
                  value={candidateForm.job_id}
                  onChange={(e) => setCandidateForm({ ...candidateForm, job_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                >
                  <option value="">Candidatura Espontânea</option>
                  {jobs.filter(j => j.status === 'open').map(job => (
                    <option key={job.job_id} value={job.job_id}>{job.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nome</label>
                <input
                  type="text"
                  value={candidateForm.name}
                  onChange={(e) => setCandidateForm({ ...candidateForm, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={candidateForm.email}
                  onChange={(e) => setCandidateForm({ ...candidateForm, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Telefone</label>
                <input
                  type="tel"
                  value={candidateForm.phone}
                  onChange={(e) => setCandidateForm({ ...candidateForm, phone: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">LinkedIn</label>
                <input
                  type="url"
                  value={candidateForm.linkedin_url}
                  onChange={(e) => setCandidateForm({ ...candidateForm, linkedin_url: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fonte</label>
                <select
                  value={candidateForm.source}
                  onChange={(e) => setCandidateForm({ ...candidateForm, source: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                >
                  <option value="internal">Interno</option>
                  <option value="referral">Referência</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="website">Website</option>
                  <option value="agency">Agência</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCandidateModal(false)}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Adicionar Candidato
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruitmentPage;
